import { flow, pipe, Runtime, TaskEither } from "fp";
import {
  browserName,
  browserVersion,
  osName,
  osVersion,
} from "react-device-detect";

import * as Context from "~/Context";
import * as ErrorMessage from "~/ErrorMessage";

import { Status as Status_ } from "./Status";

export type Request<A = Response> = TaskEither.ErrorOr<A>;
export namespace Request {
  export import Status = Status_;

  /**
   * A "request function" `Fn` waits for `Context` to create a `Request` since
   * `Context` contains the API's `baseURL` and the viewer's `token`
   */
  export type Fn<A = Response> = (context: Context.Context) => Request<A>;

  export const fromConfig = (config: Config): Fn => (context) =>
    pipe(
      Config.toFetch(context, config),
      TaskEither.mapLeft(() =>
        Error(
          `Something is wrong with your connection, ${ErrorMessage.Remedy.Retry.later()} or ${ErrorMessage.Remedy.contactSupport()}.`
        )
      )
    );

  export const onSuccess = TaskEither.map;

  export const use = <A>(fn: Fn<A>): (() => TaskEither.ErrorOr<A>) => {
    const context = Context.useContext();
    return () => fn(context);
  };

  export namespace Expect {
    export const json = <Runtime extends Runtime.Any>(runtime: Runtime) => (
      request: Request
    ): Request<Runtime.ToType<Runtime>> => {
      const toJSON = (response: Response) =>
        TaskEither.fromUnsafe(() => response.json());

      const onError = (error: Error) =>
        void console.error(error) ??
        Error(
          `Something is wrong with our data, ${ErrorMessage.Remedy.contactSupport()}.`
        );

      const decode = flow(
        Runtime.decode(runtime),
        TaskEither.fromEither,
        TaskEither.mapLeft(onError)
      );

      return pipe(request, TaskEither.chain(toJSON), TaskEither.chain(decode));
    };

    export const status = (...statuses: readonly Status[]) => (
      request: Request
    ): Request =>
      pipe(
        request,
        TaskEither.filterOrElse(
          (response) => statuses.includes(response.status),
          (response) => Error(Status.toMessage(response.status))
        )
      );
  }

  export type Config = Config.Options & { readonly endpoint: string };
  namespace Config {
    export type Options = RequestInit;

    export const toFetch = (
      context: Context.Context,
      config: Config
    ): Request => {
      const url = `${context.api.baseURL}${config.endpoint}`;
      const options = {
        ...config,
        headers: {
          ...config.headers,
          "elogs-source": "web",
          "elogs-company-id": `${context.identity.companyId}`,
          "elogs-user-id": `${context.identity.userId}`,
          "elogs-os": osName,
          "elogs-os-version": osVersion,
          "elogs-browser": browserName,
          "elogs-browser-version": browserVersion,
          Authorization: `Bearer ${context.viewer.token}`,
        },
      };

      return TaskEither.fromUnsafe(() => fetch(url, options));
    };
  }
}
